import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, FileText, Lock, Plus, Trash2, Unlock } from 'lucide-react';
import { Button, FormField, StatusBadge } from '@/components/ui';
import { Input } from '@/components/ui/input';
import type { StatusKey } from '@/types';
import { getApiErrorMessage } from '@/lib/axios';
import { listChantiers } from '@/services/chantiers';
import {
  type Decompte,
  type ModeGasoilEntrante,
  type ModeHeuresSupEntrante,
  type MotifPerte,
  HEURE_DEBUT_STD,
  HEURE_FIN_STD,
  MOTIF_LABEL,
  addAvance,
  addLigne,
  apercuHoraire,
  createDecompte,
  deleteAvance,
  deleteLigne,
  getDecompte,
  listDecomptes,
  rouvrirPointageDecompte,
  updateDecompte,
  validerDecompte,
  validerPointageDecompte,
} from '@/services/decomptesLocation';
import { getEntreprise } from '@/services/parametres';
import { toast } from '@/store/toastStore';
import { formatDZD } from '@/utils/algeria';
import { CalendrierLocation, type JourPointe } from './CalendrierLocation';

const selectCls = 'flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm';
const today = () => new Date().toISOString().slice(0, 10);
const num = (s: string) => Number(s || 0);
const STATUT_UI: Record<string, { label: string; status: StatusKey }> = {
  BROUILLON: { label: 'Brouillon · saisie ouverte', status: 'pending' },
  VALIDE: { label: 'Pointage validé (non facturé)', status: 'working' },
  FACTURE: { label: 'Facturé', status: 'done' },
};
const GASOIL_LABEL: Record<ModeGasoilEntrante, string> = {
  DEDUIT: "C'est MOI qui mets le gasoil — déduit de ce que je lui dois",
  MA_CHARGE: "Gasoil à MA charge d'emblée — rien à déduire, je suis mes frais",
  LOUEUR: 'Le loueur fournit son gasoil (non suivi)',
};

export function LocationTab({ enginId }: { enginId: string }) {
  const qc = useQueryClient();
  const [selId, setSelId] = useState<string | null>(null);
  const { data: decomptes } = useQuery({ queryKey: ['decomptes', enginId], queryFn: () => listDecomptes(enginId) });
  const { data: chantiers } = useQuery({ queryKey: ['chantiers', 'mini'], queryFn: () => listChantiers() });
  const { data: sel } = useQuery({ queryKey: ['decompte', selId], queryFn: () => getDecompte(selId as string), enabled: !!selId });

  const invalidate = () => { qc.invalidateQueries({ queryKey: ['decomptes', enginId] }); if (selId) qc.invalidateQueries({ queryKey: ['decompte', selId] }); qc.invalidateQueries({ queryKey: ['engin-synthese', enginId] }); };

  const [chantierId, setChantierId] = useState('');
  const [tarifJour, setTarifJour] = useState('');
  const [modeHS, setModeHS] = useState<ModeHeuresSupEntrante>('PRORATA');
  const [tarifHS, setTarifHS] = useState('');
  const [modeGasoil, setModeGasoil] = useState<ModeGasoilEntrante>('DEDUIT');
  const createMut = useMutation({
    mutationFn: () => createDecompte({
      engin_id: enginId, chantier_id: chantierId, periode_du: today(),
      tarif_jour: tarifJour || null, mode_heures_sup: modeHS,
      tarif_heure_sup: modeHS === 'FIXE' ? tarifHS : null, mode_gasoil: modeGasoil,
    }),
    onSuccess: (d) => { toast.success('Décompte ouvert.'); setSelId(d.id); invalidate(); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[320px_1fr]">
      {/* Colonne gauche : liste + création */}
      <div className="space-y-3">
        <div className="space-y-2 rounded-lg border border-border bg-surface-secondary p-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-content-secondary">Nouveau décompte de location</p>
          <FormField label="Chantier" required>
            <select className={selectCls} value={chantierId} onChange={(e) => setChantierId(e.target.value)}>
              <option value="">— Sélectionner —</option>
              {chantiers?.items.map((c) => <option key={c.id} value={c.id}>{c.code} · {c.nom}</option>)}
            </select>
          </FormField>
          <FormField label="Prix de location / jour (DA)" hint="Vide = tarif de la fiche engin">
            <Input type="number" step="1" min="0" value={tarifJour} onChange={(e) => setTarifJour(e.target.value)} />
          </FormField>
          <FormField label="Heures supplémentaires">
            <select className={selectCls} value={modeHS} onChange={(e) => setModeHS(e.target.value as ModeHeuresSupEntrante)}>
              <option value="PRORATA">Prorata (tarif / 8 h)</option>
              <option value="FIXE">Tarif fixe convenu</option>
            </select>
          </FormField>
          {modeHS === 'FIXE' && (
            <FormField label="Tarif heure sup (DA)" required>
              <Input type="number" step="1" min="0" value={tarifHS} onChange={(e) => setTarifHS(e.target.value)} />
            </FormField>
          )}
          <FormField label="Gasoil">
            <select className={selectCls} value={modeGasoil} onChange={(e) => setModeGasoil(e.target.value as ModeGasoilEntrante)}>
              {(Object.keys(GASOIL_LABEL) as ModeGasoilEntrante[]).map((m) => <option key={m} value={m}>{GASOIL_LABEL[m]}</option>)}
            </select>
          </FormField>
          <Button className="w-full" disabled={!chantierId || (modeHS === 'FIXE' && !num(tarifHS))} loading={createMut.isPending} onClick={() => createMut.mutate()}>
            <Plus /> Ouvrir un décompte
          </Button>
        </div>
        <div className="space-y-1">
          {(decomptes ?? []).map((d) => (
            <button key={d.id} onClick={() => setSelId(d.id)} className={`flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm ${selId === d.id ? 'border-brand-primary bg-brand-primary/5' : 'border-border hover:bg-surface-hover'}`}>
              <span className="truncate">{d.reference ?? 'Brouillon'}{d.montants_masques ? '' : ` · ${formatDZD(num(d.net_a_payer))}`}</span>
              <StatusBadge status={STATUT_UI[d.statut]?.status ?? 'pending'} label={STATUT_UI[d.statut]?.label ?? d.statut} />
            </button>
          ))}
          {(decomptes ?? []).length === 0 && <p className="px-1 py-4 text-center text-xs text-content-muted">Aucun décompte pour cet engin.</p>}
        </div>
      </div>

      {/* Colonne droite : détail du décompte sélectionné */}
      <div>{sel ? <DecompteDetail decompte={sel} onChange={invalidate} /> : <p className="py-12 text-center text-sm text-content-muted">Sélectionnez ou ouvrez un décompte.</p>}</div>
    </div>
  );
}

export function DecompteDetail({ decompte: d, onChange }: { decompte: Decompte; onChange: () => void }) {
  const readonly = d.statut !== 'BROUILLON';
  const facture = d.statut === 'FACTURE';
  const err = (e: unknown) => toast.error(getApiErrorMessage(e));
  const after = () => onChange();

  // Jours déjà pointés, pour colorer le calendrier.
  const joursPointes: Record<string, JourPointe> = {};
  for (const l of d.lignes) {
    // Plusieurs lignes le même jour (journée + plein séparé) : on les cumule, on n'en écrase aucune.
    const j = (joursPointes[l.date] ??= { journees: 0, heures_sup: 0, gasoil_litres: 0, gasoil_montant: 0 });
    j.journees += num(l.journees);
    j.heures_sup += num(l.heures_sup);
    j.gasoil_litres = (j.gasoil_litres ?? 0) + num(l.gasoil_litres);
    j.gasoil_montant = (j.gasoil_montant ?? 0) + num(l.gasoil_avance);
    if (l.heure_debut && l.heure_fin && !j.heure_debut) {
      j.heure_debut = l.heure_debut; j.heure_fin = l.heure_fin; j.motif_perte = l.motif_perte;
    }
  }

  const masque = d.montants_masques;                       // pas de droit « tarifs » : aucun montant
  const gasoilMien = d.mode_gasoil !== 'LOUEUR';           // gasoil fourni par moi : suivi ; celui du loueur, non
  const { data: entreprise } = useQuery({ queryKey: ['entreprise'], queryFn: getEntreprise });
  const prixLitre = Number(entreprise?.prix_carburant_defaut ?? 0);
  const [plein, setPlein] = useState({ date: today(), litres: '', cout: '' });
  const coutPropose = plein.litres && prixLitre ? String(Math.round(Number(plein.litres) * prixLitre)) : '';
  const LIGNE_VIDE = { date: today(), journees: '1', heures_sup: '0', gasoil_avance: '0', gasoil_litres: '0',
    chauffeur_nom: '', lieu: '', heure_debut: '', heure_fin: '', motif_perte: '' as MotifPerte | '' };
  const [ligne, setLigne] = useState(LIGNE_VIDE);
  const horaire = apercuHoraire(ligne.heure_debut, ligne.heure_fin);   // aperçu ; le serveur fait foi
  const saisieHoraire = !!ligne.heure_debut && !!ligne.heure_fin;
  const [avance, setAvance] = useState({ date_avance: today(), montant: '' });

  const addLigneMut = useMutation({
    mutationFn: () => addLigne(d.id, {
      ...ligne,
      // Horaires saisis → le serveur calcule journées et h. sup ; sinon saisie classique en journées.
      heure_debut: saisieHoraire ? ligne.heure_debut : null,
      heure_fin: saisieHoraire ? ligne.heure_fin : null,
      motif_perte: saisieHoraire && horaire && horaire.perdues > 0 ? (ligne.motif_perte || null) : null,
    }),
    onSuccess: () => { setLigne(LIGNE_VIDE); after(); },
    onError: err,
  });
  const pleinMut = useMutation({
    mutationFn: () => addLigne(d.id, {
      date: plein.date, journees: '0', heures_sup: '0',
      gasoil_litres: plein.litres, gasoil_avance: plein.cout || coutPropose || '0',
    }),
    onSuccess: () => { toast.success('Plein enregistré.'); setPlein({ date: today(), litres: '', cout: '' }); after(); },
    onError: err,
  });
  const modeMut = useMutation({ mutationFn: (m: ModeGasoilEntrante) => updateDecompte(d.id, { mode_gasoil: m }), onSuccess: after, onError: err });
  const delLigneMut = useMutation({ mutationFn: (id: string) => deleteLigne(id), onSuccess: after, onError: err });
  const addAvanceMut = useMutation({ mutationFn: () => addAvance(d.id, avance), onSuccess: () => { setAvance({ date_avance: today(), montant: '' }); after(); }, onError: err });
  const delAvanceMut = useMutation({ mutationFn: (id: string) => deleteAvance(id), onSuccess: after, onError: err });
  const validerMut = useMutation({ mutationFn: () => validerDecompte(d.id), onSuccess: () => { toast.success('Facture fournisseur générée sur le net.'); after(); }, onError: err });
  const validerPointageMut = useMutation({ mutationFn: () => validerPointageDecompte(d.id), onSuccess: () => { toast.success('Pointage validé (aucune facture générée).'); after(); }, onError: err });
  const rouvrirMut = useMutation({ mutationFn: () => rouvrirPointageDecompte(d.id), onSuccess: () => { toast.success('Pointage rouvert.'); after(); }, onError: err });

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-bold text-content-primary">
          {d.reference ?? 'Décompte (brouillon)'}
          <span className="ml-2 text-sm font-normal text-content-muted">
            {masque ? '' : `${formatDZD(num(d.tarif_jour))}/jour · h. sup ${formatDZD(num(d.tarif_heure_applique))} (${d.mode_heures_sup === 'FIXE' ? 'fixe' : 'prorata'}) · `}gasoil : {GASOIL_LABEL[d.mode_gasoil]}
          </span>
        </h3>
        <StatusBadge status={STATUT_UI[d.statut]?.status ?? 'pending'} label={STATUT_UI[d.statut]?.label ?? d.statut} variant="solid" />
      </div>

      {!readonly && (
        <label className="block text-xs text-content-secondary">Gasoil de cette location
          <select className={selectCls} value={d.mode_gasoil} disabled={modeMut.isPending}
                  onChange={(e) => modeMut.mutate(e.target.value as ModeGasoilEntrante)}>
            {(Object.keys(GASOIL_LABEL) as ModeGasoilEntrante[]).map((m) => <option key={m} value={m}>{GASOIL_LABEL[m]}</option>)}
          </select>
        </label>
      )}

      {/* Saisie journalière facturable */}
      {/* Calendrier de pointage : on clique le jour où la machine louée a travaillé. */}
      <CalendrierLocation
        documentId={d.id}
        sens="ENTRANTE"
        jours={joursPointes}
        moisInitial={d.periode_du}
        modifiable={!readonly}
        onChange={after}
        gasoil={d.mode_gasoil === 'LOUEUR' ? null : {
          libelleMode: d.mode_gasoil === 'MA_CHARGE' ? 'à ma charge, non déduit au loueur' : 'déduit de ce que je dois au loueur',
          prixLitre, masque,
        }}
      />

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border bg-surface-primary/40 text-left text-xs text-content-muted">
            <th className="px-3 py-2">Date</th><th className="px-2 py-2">Horaires</th><th className="px-2 py-2 text-center">Journées</th><th className="px-2 py-2 text-center">H. sup</th>
            <th className="px-3 py-2 text-right">Gasoil</th><th className="px-3 py-2">Chauffeur</th><th className="px-3 py-2">Lieu</th>
            {!masque && <th className="px-3 py-2 text-right">Montant</th>}<th /></tr></thead>
          <tbody>
            {d.lignes.map((l) => (
              <tr key={l.id} className="border-b border-border/60 last:border-0">
                <td className="px-3 py-1.5">{l.date.slice(8, 10)}/{l.date.slice(5, 7)}/{l.date.slice(0, 4)}</td>
                <td className="px-2 py-1.5 text-xs text-content-secondary">
                  {l.heure_debut && l.heure_fin ? (
                    <>
                      {l.heure_debut.slice(0, 5)} → {l.heure_fin.slice(0, 5)}
                      {num(l.heures_perdues) > 0 && l.motif_perte && (
                        <span className="block text-[10px] text-content-muted">
                          {num(l.heures_perdues)} h perdue(s) · {l.motif_perte === 'ATTENTE_AWC' ? 'facturée (attente AWC)' : l.motif_perte === 'INTEMPERIES' ? 'intempéries, déduite' : 'panne, déduite'}
                        </span>
                      )}
                    </>
                  ) : '—'}
                </td>
                <td className="px-2 py-1.5 text-center">{Math.round(num(l.journees) * 1000) / 1000}</td>
                <td className="px-2 py-1.5 text-center">{num(l.heures_sup) || '—'}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">
                  {!masque && num(l.gasoil_avance) ? formatDZD(num(l.gasoil_avance)) : (num(l.gasoil_litres) ? '' : '—')}
                  {num(l.gasoil_litres) ? <span className="block text-[10px] text-content-muted">{num(l.gasoil_litres)} L</span> : null}
                </td>
                <td className="px-3 py-1.5 text-content-secondary">{l.chauffeur_nom ?? '—'}</td>
                <td className="px-3 py-1.5 text-content-secondary">{l.lieu ?? '—'}</td>
                {!masque && <td className="px-3 py-1.5 text-right tabular-nums">{formatDZD(num(l.montant))}</td>}
                <td className="px-2 py-1.5 text-right">{!readonly && <button onClick={() => delLigneMut.mutate(l.id)} className="text-content-muted hover:text-status-stuck" aria-label="Supprimer"><Trash2 className="size-4" /></button>}</td>
              </tr>
            ))}
            {d.lignes.length === 0 && <tr><td colSpan={9} className="px-3 py-4 text-center text-xs text-content-muted">Aucune journée saisie.</td></tr>}
          </tbody>
        </table>
      </div>
      {!readonly && (
        <div className="space-y-2 rounded-lg border border-border bg-surface-secondary p-3">
          <p className="text-[11px] text-content-muted">
            Journée de référence <strong>{HEURE_DEBUT_STD} → {HEURE_FIN_STD}</strong> (8 h, pause ignorée).
            Saisissez l'heure de début et de fin : les heures perdues sont déduites (panne, intempéries) et les heures après {HEURE_FIN_STD} comptées en heures supplémentaires.
            Pour cocher simplement les jours travaillés, utilisez le calendrier ci-dessus.
          </p>
          <div className="grid grid-cols-2 items-end gap-2 sm:grid-cols-4 lg:grid-cols-6">
            <FormField label="Date"><Input type="date" value={ligne.date} onChange={(e) => setLigne({ ...ligne, date: e.target.value })} /></FormField>
            <FormField label="Heure de début"><Input type="time" value={ligne.heure_debut} onChange={(e) => setLigne({ ...ligne, heure_debut: e.target.value })} /></FormField>
            <FormField label="Heure de fin"><Input type="time" value={ligne.heure_fin} onChange={(e) => setLigne({ ...ligne, heure_fin: e.target.value })} /></FormField>
            {saisieHoraire && horaire && horaire.perdues > 0 && (
              <FormField label="Motif des heures perdues" required className="col-span-2">
                <select className={selectCls} value={ligne.motif_perte} onChange={(e) => setLigne({ ...ligne, motif_perte: e.target.value as MotifPerte })}>
                  <option value="">— Sélectionner —</option>
                  {(Object.keys(MOTIF_LABEL) as MotifPerte[]).map((m) => <option key={m} value={m}>{MOTIF_LABEL[m]}</option>)}
                </select>
              </FormField>
            )}
            {!saisieHoraire && (
              <>
                <FormField label="Journées"><select className={selectCls} value={ligne.journees} onChange={(e) => setLigne({ ...ligne, journees: e.target.value })}><option value="1">1 (pleine)</option><option value="0.5">0,5 (demi)</option><option value="0">0</option></select></FormField>
                <FormField label="H. sup"><Input type="number" step="1" min="0" value={ligne.heures_sup} onChange={(e) => setLigne({ ...ligne, heures_sup: e.target.value })} /></FormField>
              </>
            )}
            {gasoilMien && (
              <>
                {!masque && <FormField label="Gasoil (DA)"><Input type="number" step="1" min="0" value={ligne.gasoil_avance} onChange={(e) => setLigne({ ...ligne, gasoil_avance: e.target.value })} /></FormField>}
                <FormField label="Litres versés"><Input type="number" step="1" min="0" value={ligne.gasoil_litres} onChange={(e) => setLigne({ ...ligne, gasoil_litres: e.target.value })} /></FormField>
              </>
            )}
            <FormField label="Chauffeur"><Input placeholder="Nom" value={ligne.chauffeur_nom} onChange={(e) => setLigne({ ...ligne, chauffeur_nom: e.target.value })} /></FormField>
            <FormField label="Lieu"><Input value={ligne.lieu} onChange={(e) => setLigne({ ...ligne, lieu: e.target.value })} /></FormField>
            <Button
              disabled={saisieHoraire && (!horaire || (horaire.perdues > 0 && !ligne.motif_perte))}
              loading={addLigneMut.isPending} onClick={() => addLigneMut.mutate()}><Plus /> Ajouter</Button>
          </div>
          {saisieHoraire && !horaire && <p className="text-xs text-status-stuck">L'heure de fin doit être postérieure à l'heure de début.</p>}
          {horaire && (
            <p className="text-xs text-content-secondary">
              {horaire.perdues > 0 && <>{horaire.perdues} h perdue(s) sur 8 h. </>}
              {horaire.sup > 0 && <>{horaire.sup} h supplémentaire(s) après {HEURE_FIN_STD}. </>}
              {horaire.perdues === 0 && horaire.sup === 0 && <>Journée complète.</>}
            </p>
          )}
          {!gasoilMien && <p className="text-[11px] text-content-muted">Le loueur fournit son gasoil : il n'est pas enregistré.</p>}
        </div>
      )}

      {/* Plein de gasoil : à chaque remplissage, quelle que soit la journée (même non travaillée) */}
      {gasoilMien && !readonly && (
        <div className="rounded-lg border border-border bg-surface-secondary p-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-content-secondary">J'ai mis du gasoil</p>
          <div className="grid grid-cols-2 items-end gap-2 sm:grid-cols-4">
            <FormField label="Date"><Input type="date" value={plein.date} onChange={(e) => setPlein({ ...plein, date: e.target.value })} /></FormField>
            <FormField label="Litres"><Input type="number" step="1" min="0" value={plein.litres} onChange={(e) => setPlein({ ...plein, litres: e.target.value })} /></FormField>
            {!masque && (
              <FormField label="Coût (DA)" hint={prixLitre ? `Proposé : litres × ${prixLitre} DA` : undefined}>
                <Input type="number" step="1" min="0" placeholder={coutPropose} value={plein.cout} onChange={(e) => setPlein({ ...plein, cout: e.target.value })} />
              </FormField>
            )}
            <Button disabled={!num(plein.litres)} loading={pleinMut.isPending} onClick={() => pleinMut.mutate()}><Plus /> Enregistrer le plein</Button>
          </div>
          <p className="mt-1.5 text-[11px] text-content-muted">
            {d.mode_gasoil === 'MA_CHARGE'
              ? "Gasoil à votre charge d'emblée : enregistré pour suivre vos frais, il n'est jamais déduit de ce que vous devez au loueur."
              : 'Gasoil avancé : déduit de ce que vous devez au loueur, et compté dans vos frais.'}
          </p>
        </div>
      )}

      {/* Contrôle du gasoil À MA CHARGE : combien j'ai versé, et la consommation qui en découle */}
      {gasoilMien && (
        <div className={`rounded-lg border p-3 text-sm ${d.alerte_surconso ? 'border-status-stuck/50 bg-status-stuck/5' : 'border-border bg-surface-secondary'}`}>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-content-secondary">Gasoil à ma charge — contrôle</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <div><p className="text-[10px] uppercase text-content-muted">Litres versés</p><p className="font-semibold tabular-nums">{num(d.gasoil_litres_total)} L</p></div>
            {!masque && (
              <div>
                <p className="text-[10px] uppercase text-content-muted">Mes frais de gasoil</p>
                <p className="font-semibold tabular-nums">{formatDZD(num(d.gasoil_cout_total))}</p>
                <p className="text-[10px] text-content-muted">
                  {d.mode_gasoil === 'DEDUIT' ? 'déduits du loueur' : 'rien déduit au loueur'}
                  {d.gasoil_cout_par_heure ? ` · ${formatDZD(num(d.gasoil_cout_par_heure))}/h` : ''}
                </p>
              </div>
            )}
            <div><p className="text-[10px] uppercase text-content-muted">Heures travaillées</p><p className="font-semibold tabular-nums">{num(d.heures_travaillees)} h</p></div>
            <div><p className="text-[10px] uppercase text-content-muted">Consommation</p><p className="font-semibold tabular-nums">{d.conso_lh ? `${num(d.conso_lh)} L/h` : '—'}{d.conso_reference_lh ? <span className="text-[10px] font-normal text-content-muted"> (réf. {num(d.conso_reference_lh)})</span> : null}</p></div>
          </div>
          {d.alerte_surconso && (
            <p className="mt-2 flex items-center gap-1 text-xs font-medium text-status-stuck">
              <AlertTriangle className="size-4" /> Surconsommation : plus de 30 % au-dessus de la référence de l'engin — à vérifier.
            </p>
          )}
        </div>
      )}

      {/* Avances versées au loueur */}
      {!masque && <div>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-content-secondary">Avances versées au loueur (espèces)</p>
        <div className="space-y-1">
          {d.avances.map((a) => (
            <div key={a.id} className="flex items-center justify-between rounded-md border border-border px-3 py-1.5 text-sm">
              <span>{a.date_avance} · {formatDZD(num(a.montant))}</span>
              {!readonly && <button onClick={() => delAvanceMut.mutate(a.id)} className="text-content-muted hover:text-status-stuck" aria-label="Supprimer"><Trash2 className="size-4" /></button>}
            </div>
          ))}
          {d.avances.length === 0 && <p className="text-xs text-content-muted">Aucune avance.</p>}
        </div>
        {!readonly && (
          <div className="mt-2 grid grid-cols-2 items-end gap-2 sm:grid-cols-3">
            <FormField label="Date"><Input type="date" value={avance.date_avance} onChange={(e) => setAvance({ ...avance, date_avance: e.target.value })} /></FormField>
            <FormField label="Montant (DA)"><Input type="number" step="1" min="0" value={avance.montant} onChange={(e) => setAvance({ ...avance, montant: e.target.value })} /></FormField>
            <Button variant="secondary" disabled={!num(avance.montant)} loading={addAvanceMut.isPending} onClick={() => addAvanceMut.mutate()}><Plus /> Avance</Button>
          </div>
        )}
      </div>}

      {/* Récap net à payer */}
      {!masque && <div className="rounded-lg border border-border bg-surface-secondary p-4 text-sm">
        <Row label="Brut (loyer)" value={formatDZD(num(d.total_brut))} />
        <Row label="− Gasoil avancé" value={`− ${formatDZD(num(d.total_gasoil))}`} tone="stuck" />
        <Row label="− Avances versées" value={`− ${formatDZD(num(d.total_avances))}`} tone="stuck" />
        <div className="mt-2 flex items-center justify-between border-t border-border pt-2 text-base font-bold">
          <span>Net à payer</span><span className="tabular-nums text-brand-primary">{formatDZD(num(d.net_a_payer))}</span>
        </div>
      </div>}
      {masque && (
        <p className="rounded-md border border-border bg-surface-secondary px-3 py-2 text-xs text-content-muted">
          Votre profil permet de pointer et de consulter les pointages ; les tarifs et montants de location ne vous sont pas communiqués.
        </p>
      )}

      {facture && (
        <p className="rounded-md border border-status-done/40 bg-status-done/10 px-3 py-2 text-sm text-content-primary">
          <FileText className="mr-1 inline size-4" /> Facture fournisseur {d.facture_reference ? <strong>{d.facture_reference}</strong> : 'brouillon'} générée sur le net. Réglez-la via le circuit Factures fournisseur.
        </p>
      )}
      {d.statut === 'VALIDE' && (
        <p className="rounded-md border border-status-working/40 bg-status-working/10 px-3 py-2 text-sm text-content-primary">
          Pointage validé, <strong>sans facture</strong> (location non déclarée). Vous pouvez encore générer la facture ou rouvrir le pointage.
        </p>
      )}
      {!masque && <div className="flex flex-wrap gap-2">
        {!readonly && (
          <>
            <Button variant="secondary" disabled={num(d.total_brut) <= 0} loading={validerPointageMut.isPending} onClick={() => validerPointageMut.mutate()}>
              <Lock /> Valider le pointage (sans facture)
            </Button>
            <Button className="flex-1" disabled={num(d.total_brut) <= 0} loading={validerMut.isPending} onClick={() => validerMut.mutate()}>
              <FileText /> Générer la facture fournisseur (net {formatDZD(num(d.net_a_payer))})
            </Button>
          </>
        )}
        {d.statut === 'VALIDE' && (
          <Button className="flex-1" disabled={num(d.total_brut) <= 0} loading={validerMut.isPending} onClick={() => validerMut.mutate()}>
            <FileText /> Générer la facture fournisseur (net {formatDZD(num(d.net_a_payer))})
          </Button>
        )}
        {readonly && (
          <Button variant="secondary" loading={rouvrirMut.isPending}
            onClick={() => { if (window.confirm(facture ? 'Rouvrir le pointage ? La facture brouillon liée sera supprimée.' : 'Rouvrir le pointage pour correction ?')) rouvrirMut.mutate(); }}>
            <Unlock /> Rouvrir le pointage
          </Button>
        )}
      </div>}
    </div>
  );
}

function Row({ label, value, tone }: { label: string; value: string; tone?: 'stuck' }) {
  return (
    <div className="flex items-center justify-between py-0.5">
      <span className="text-content-secondary">{label}</span>
      <span className={`tabular-nums ${tone === 'stuck' ? 'text-status-stuck' : 'text-content-primary'}`}>{value}</span>
    </div>
  );
}
